import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  RotateCcw,
  FileDown,
  Layers,
  Clock,
  CheckCircle2,
  XCircle,
  Search,
  X,
  Eye,
  MessageSquare,
  Settings,
  Download,
  FileText,
  MoreHorizontal,
  ToggleLeft,
} from 'lucide-react';
import BaseTable from '../../components/base/BaseTable';
import BaseFilter from '../../components/base/BaseFilter';
import ExtensionApproveModal from '../../components/report/ExtensionApproveModal';
import ReportDetailModal from '../../components/report/ReportDetailModal';
import { ExtensionService } from '../../services/extensionService';
import { REPORT_API } from '../../apis/report';
import { showToast } from '../../utils/toastNotification';
import ROUTE_PATH from '../../constants/routes';
import dayjs from 'dayjs';

const ALL_EXTENSION_COLUMNS = [
  { key: "index", title: "STT" },
  { key: "complaintCode", title: "Mã PA" },
  { key: "complaintTitle", title: "Tiêu đề phản ánh" },
  { key: "neighborhood", title: "Khu phố" },
  { key: "category", title: "Lĩnh vực" },
  { key: "requestedBy", title: "Người đề nghị" },
  { key: "requestedAt", title: "Ngày đề nghị" },
  { key: "oldDeadline", title: "Hạn ban đầu" },
  { key: "requestedDeadline", title: "Hạn đề xuất mới" },
  { key: "status", title: "Trạng thái" },
];

export default function ReportExtensions() {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Single active Action Menu across table
  const [activeActionMenu, setActiveActionMenu] = useState(null);

  useEffect(() => {
    if (!activeActionMenu) return;

    const handleClose = () => setActiveActionMenu(null);
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setActiveActionMenu(null);
    };

    document.addEventListener('click', handleClose);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);

    return () => {
      document.removeEventListener('click', handleClose);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [activeActionMenu]);

  const handleToggleActionMenu = (e, record) => {
    e.stopPropagation();
    const rowKey = record.id || record.ma_phan_anh;
    if (activeActionMenu?.rowKey === rowKey) {
      setActiveActionMenu(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 160;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    setActiveActionMenu({
      rowKey,
      record,
      style: {
        position: 'fixed',
        top: openUpward ? `${rect.top - 6}px` : `${rect.bottom + 6}px`,
        right: `${Math.max(12, window.innerWidth - rect.right)}px`,
        transform: openUpward ? 'translateY(-100%)' : 'none',
        zIndex: 99999,
      }
    });
  };

  // Filters
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUrgency, setSelectedUrgency] = useState('ALL');
  const [selectedDepartment, setSelectedDepartment] = useState('ALL');

  // Modal Thẩm định gia hạn
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);

  // Modal Xem chi tiết phản ánh
  const [selectedReportDetail, setSelectedReportDetail] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailModalMode, setDetailModalMode] = useState('view');

  const handleOpenDetailModal = async (record, mode = 'view') => {
    setDetailModalMode(mode);
    const complaintId = record.id_phan_anh || record.phan_anh?.id || record.complaintId;
    const reasonText = record.ly_do_gia_han || record.reason || record.ly_do;
    try {
      if (complaintId) {
        const fullData = await REPORT_API.getReportById(complaintId);
        setSelectedReportDetail({
          ...(fullData || record.phan_anh || record),
          id: fullData?.id || complaintId,
          ly_do_gia_han: reasonText || fullData?.ly_do_gia_han
        });
      } else {
        setSelectedReportDetail({
          ...(record.phan_anh || record),
          id: record.id_phan_anh || record.phan_anh?.id || record.complaintId || record.id,
          ly_do_gia_han: reasonText
        });
      }
    } catch (err) {
      setSelectedReportDetail({
        ...(record.phan_anh || record),
        id: record.id_phan_anh || record.phan_anh?.id || record.complaintId || record.id,
        ly_do_gia_han: reasonText
      });
    } finally {
      setIsDetailModalOpen(true);
    }
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [stats, setStats] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });

  const fetchStats = async () => {
    try {
      const data = await ExtensionService.getStats();
      if (data) {
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching extension stats:', err);
    }
  };

  const fetchExtensions = async () => {
    setLoading(true);
    try {
      const [data] = await Promise.all([
        ExtensionService.getAll(),
        fetchStats(),
      ]);
      setExtensions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching extensions:', err);
      showToast.error('Không thể tải danh sách đề nghị gia hạn');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExtensions();
  }, []);

  const handleRefresh = async () => {
    await fetchExtensions();
    showToast.success('Đã làm mới danh sách đề nghị gia hạn');
  };

  // Stat Counts: ưu tiên từ API stats, fallback sang tính từ extensions
  const statCounts = useMemo(() => {
    if (stats && (stats.all > 0 || stats.pending > 0 || stats.approved > 0 || stats.rejected > 0)) {
      return stats;
    }
    const all = extensions.length;
    const pending = extensions.filter(e => (e.status || e.trang_thai) === 'PENDING').length;
    const approved = extensions.filter(e => (e.status || e.trang_thai) === 'APPROVED').length;
    const rejected = extensions.filter(e => (e.status || e.trang_thai) === 'REJECTED').length;
    return { all, pending, approved, rejected };
  }, [stats, extensions]);

  // Unique departments for filter
  const departments = useMemo(() => {
    const set = new Set();
    extensions.forEach(e => {
      if (e.category) set.add(e.category);
      if (e.department) set.add(e.department);
    });
    return Array.from(set);
  }, [extensions]);

  // Column visibility setting state
  const [visibleColumns, setVisibleColumns] = useState(() =>
    ALL_EXTENSION_COLUMNS.map((c) => c.key)
  );
  const [showColumnSetting, setShowColumnSetting] = useState(false);
  const columnSettingRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnSettingRef.current && !columnSettingRef.current.contains(event.target)) {
        setShowColumnSetting(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleColumn = (colKey) => {
    setVisibleColumns((prev) => {
      if (prev.includes(colKey)) {
        if (prev.length <= 1) {
          showToast.warning("Phải giữ ít nhất 1 cột hiển thị!");
          return prev;
        }
        return prev.filter((k) => k !== colKey);
      } else {
        return [...prev, colKey];
      }
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(ALL_EXTENSION_COLUMNS.map((c) => c.key));
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExportExcel = async () => {
    if (isExporting) return;
    if (filteredData.length === 0) {
      showToast.error("Không có dữ liệu đề nghị gia hạn để xuất Excel");
      return;
    }
    setIsExporting(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "ALL") {
        params.status = statusFilter;
      }
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      if (selectedUrgency && selectedUrgency !== "ALL") {
        params.mucDo = selectedUrgency;
      }
      if (selectedDepartment && selectedDepartment !== "ALL") {
        params.idLinhVuc = selectedDepartment;
      }

      const res = await ExtensionService.exportExcel(params);
      if (res.success && res.data) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `quan_ly_gia_han_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        showToast.success("Xuất file Excel danh sách gia hạn thành công!");
      } else {
        showToast.error(res.error || "Xuất file Excel thất bại");
      }
    } catch (err) {
      console.error("Lỗi xuất Excel gia hạn:", err);
      showToast.error("Có lỗi xảy ra khi xuất file Excel");
    } finally {
      setIsExporting(false);
    }
  };

  const columnSettingComponent = (
    <div className="flex items-center gap-2">
      {/* Nút Xuất Excel */}
      <button
        type="button"
        onClick={handleExportExcel}
        disabled={isExporting}
        className="px-3 py-2 bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-60 text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 shadow-xs cursor-pointer disabled:cursor-not-allowed"
        title="Xuất danh sách đề nghị gia hạn ra file Excel"
      >
        {isExporting ? (
          <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
        ) : (
          <Download className="w-4 h-4 text-gray-500" />
        )}
        <span className="hidden sm:inline">Xuất Excel</span>
      </button>

      {/* Nút Tùy chỉnh cột hiển thị */}
      <div className="relative flex-shrink-0" ref={columnSettingRef}>
        <button
          type="button"
          onClick={() => setShowColumnSetting((prev) => !prev)}
          className={`p-2 border rounded-lg transition-all flex items-center justify-center cursor-pointer ${showColumnSetting
              ? "bg-blue-50 border-blue-400 text-blue-600 shadow-xs"
              : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            }`}
          title="Tùy chỉnh cột hiển thị"
        >
          <Settings className={`w-5 h-5 ${showColumnSetting ? "text-blue-600" : "text-gray-600"}`} />
        </button>

        {showColumnSetting && (
          <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 p-3 z-50">
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Cột hiển thị ({visibleColumns.length}/{ALL_EXTENSION_COLUMNS.length})
              </span>
              <button
                type="button"
                onClick={handleResetColumns}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline cursor-pointer"
              >
                Mặc định
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto space-y-1 py-1">
              {ALL_EXTENSION_COLUMNS.map((col) => {
                const isChecked = visibleColumns.includes(col.key);
                return (
                  <label
                    key={col.key}
                    className={`flex items-center justify-between px-2 py-1.5 rounded-lg text-sm transition-colors select-none cursor-pointer ${isChecked ? "hover:bg-blue-50/60 text-gray-800" : "hover:bg-gray-50 text-gray-500"
                      }`}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleColumn(col.key)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="font-normal truncate">{col.title}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Filtered dataset
  const filteredData = useMemo(() => {
    const list = extensions.filter(item => {
      // Status filter
      if (statusFilter !== 'ALL' && (item.status || item.trang_thai) !== statusFilter) {
        return false;
      }

      // Urgency filter
      if (selectedUrgency !== 'ALL' && item.urgency !== selectedUrgency) {
        return false;
      }

      // Department filter
      if (selectedDepartment !== 'ALL') {
        const matchDept = item.category === selectedDepartment || item.department === selectedDepartment;
        if (!matchDept) return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const code = (item.complaintCode || item.ma_phan_anh || '').toLowerCase();
        const title = (item.complaintTitle || item.tieu_de_phan_anh || '').toLowerCase();
        const by = (item.requestedBy || item.nguoi_de_nghi?.ho_va_ten || '').toLowerCase();
        const reason = (item.reason || item.ly_do_gia_han || '').toLowerCase();
        const kp = (item.neighborhood || item.khu_pho || '').toLowerCase();

        return code.includes(query) || title.includes(query) || by.includes(query) || reason.includes(query) || kp.includes(query);
      }

      return true;
    });

    // Sắp xếp: Ưu tiên "Chờ phê duyệt" (PENDING) lên trên cùng, sau đó sắp xếp theo thời gian mới nhất
    return list.sort((a, b) => {
      const isPendingA = (a.status === 'PENDING' || a.trang_thai === 'PENDING') ? 1 : 0;
      const isPendingB = (b.status === 'PENDING' || b.trang_thai === 'PENDING') ? 1 : 0;
      if (isPendingA !== isPendingB) {
        return isPendingB - isPendingA;
      }
      const timeA = new Date(a.thoi_gian_de_nghi || a.requestedAt || a.thoi_gian_tao || 0).getTime();
      const timeB = new Date(b.thoi_gian_de_nghi || b.requestedAt || b.thoi_gian_tao || 0).getTime();
      return timeB - timeA;
    });
  }, [extensions, statusFilter, selectedUrgency, selectedDepartment, searchQuery]);

  // Paginated dataset
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const handleResetFilters = () => {
    setStatusFilter('ALL');
    setSearchQuery('');
    setSelectedUrgency('ALL');
    setSelectedDepartment('ALL');
    setCurrentPage(1);
  };

  const handleOpenApproveModal = (record) => {
    setSelectedExtension(record);
    setIsApproveModalOpen(true);
  };

  const filterFields = [
    {
      name: 'searchQuery',
      label: 'Mã phản ánh',
      type: 'search',
      placeholder: 'Tìm theo mã phản ánh...',
    },
    {
      name: 'statusFilter',
      label: 'Trạng thái',
      type: 'select',
      options: [
        { value: 'ALL', label: 'Tất cả trạng thái' },
        { value: 'PENDING', label: 'Chờ phê duyệt' },
        { value: 'APPROVED', label: 'Đã phê duyệt' },
        { value: 'REJECTED', label: 'Đã từ chối' },
      ],
    },
    {
      name: 'selectedUrgency',
      label: 'Mức độ',
      type: 'select',
      options: [
        { value: 'ALL', label: 'Tất cả mức độ' },
        { value: 'Thông thường', label: 'Thông thường' },
        { value: 'Khẩn cấp', label: 'Khẩn cấp' },
      ],
    },
    {
      name: 'selectedDepartment',
      label: 'Lĩnh vực',
      type: 'select',
      options: [
        { value: 'ALL', label: 'Tất cả lĩnh vực' },
        ...departments.map((dept) => ({ value: dept, label: dept })),
      ],
    },
  ];

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: '60px',
      render: (_, __, index) => (
        <span className="text-xs font-semibold text-gray-700">
          #{(currentPage - 1) * pageSize + index + 1}
        </span>
      ),
    },
    {
      title: 'Mã PA',
      key: 'complaintCode',
      width: '100px',
      render: (_, record) => {
        const code = record.ma_phan_anh || record.complaintCode || '-';
        return (
          <span className="inline-flex px-2 py-0.5 text-xs font-mono font-bold bg-gray-100 text-gray-800 border border-gray-200 rounded whitespace-nowrap">
            {code}
          </span>
        );
      },
    },
    {
      title: 'Tiêu đề & Nội dung phản ánh',
      key: 'complaintTitle',
      width: '280px',
      render: (_, record) => {
        const title = record.tieu_de_phan_anh || record.complaintTitle || 'Phản ánh kiến nghị';
        const reason = record.ly_do_gia_han || record.reason || '-';
        return (
          <div className="py-0.5 max-w-[280px]">
            <div
              onClick={() => handleOpenDetailModal(record)}
              className="text-xs md:text-sm font-semibold text-gray-900 hover:text-blue-600 cursor-pointer transition-colors leading-snug truncate"
              title={title}
            >
              {title}
            </div>
            <div className="text-xs text-gray-500 mt-0.5 truncate italic" title={`Lý do: ${reason}`}>
              Lý do: {reason}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Khu phố',
      key: 'neighborhood',
      width: '90px',
      render: (_, record) => {
        const kp = record.khu_pho || record.neighborhood || 'Chưa rõ KP';
        return <span className="font-semibold text-xs text-gray-800 whitespace-nowrap">{kp}</span>;
      },
    },
    {
      title: 'Lĩnh vực',
      key: 'category',
      width: '165px',
      render: (_, record) => {
        const lv = record.linh_vuc?.ten || record.category || 'Chung';
        return (
          <span
            className="inline-block max-w-[150px] truncate px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[11px] font-medium border border-blue-100 align-middle"
            title={lv}
          >
            {lv}
          </span>
        );
      },
    },
    {
      title: 'Người đề nghị',
      key: 'requestedBy',
      width: '145px',
      render: (_, record) => {
        const by = record.nguoi_de_nghi?.ho_va_ten || record.nguoi_de_nghi?.ten_dang_nhap || record.requestedBy || 'Cán bộ';
        return (
          <span className="text-xs font-semibold text-gray-900 truncate block max-w-[135px]" title={by}>
            {by}
          </span>
        );
      },
    },
    {
      title: 'Ngày đề nghị',
      key: 'requestedAt',
      width: '125px',
      render: (_, record) => {
        const at = record.thoi_gian_de_nghi || record.requestedAt;
        if (!at) return <span className="text-xs text-gray-400 font-medium">-</span>;
        const d = dayjs(at);
        return (
          <div className="flex flex-col text-xs leading-tight whitespace-nowrap">
            <span className="text-gray-800 font-medium">{d.format('HH:mm')}</span>
            <span className="text-gray-500 text-[11px]">{d.format('DD/MM/YYYY')}</span>
          </div>
        );
      },
    },
    {
      title: 'Hạn ban đầu',
      key: 'oldDeadline',
      width: '125px',
      render: (_, record) => {
        const val = record.han_ban_dau || record.oldDeadline;
        if (!val) return <span className="text-xs text-gray-400 font-medium">-</span>;
        const d = dayjs(val);
        return (
          <div className="flex flex-col text-xs leading-tight text-rose-600 line-through whitespace-nowrap">
            <span className="font-semibold">{d.format('HH:mm')}</span>
            <span className="text-[11px]">{d.format('DD/MM/YYYY')}</span>
          </div>
        );
      },
    },
    {
      title: 'Hạn đề xuất mới',
      key: 'requestedDeadline',
      width: '135px',
      render: (_, record) => {
        const val = record.han_de_xuat_moi || record.requestedDeadline;
        if (!val) return <span className="text-xs text-gray-400 font-medium">-</span>;
        const d = dayjs(val);
        return (
          <div className="inline-flex flex-col text-xs leading-tight px-2 py-0.5 bg-blue-50 text-blue-700 font-bold rounded border border-blue-200 whitespace-nowrap">
            <span>{d.format('HH:mm')}</span>
            <span className="text-[11px] font-medium text-blue-600">{d.format('DD/MM/YYYY')}</span>
          </div>
        );
      },
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: '145px',
      render: (_, record) => {
        const val = record.trang_thai || record.status;
        if (val === 'APPROVED' || val === 'Đã duyệt') {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200 whitespace-nowrap">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              Đã phê duyệt
            </span>
          );
        }
        if (val === 'REJECTED' || val === 'Từ chối') {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 text-xs font-bold rounded-full border border-red-200 whitespace-nowrap">
              <XCircle className="w-3.5 h-3.5 shrink-0" />
              Đã từ chối
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-700 text-xs font-bold rounded-full border border-orange-200 animate-pulse whitespace-nowrap">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            Chờ phê duyệt
          </span>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: '80px',
      render: (_, record) => {
        const rowKey = record.id || record.ma_phan_anh;
        const isOpen = activeActionMenu?.rowKey === rowKey;
        return (
          <button
            type="button"
            onClick={(e) => handleToggleActionMenu(e, record)}
            className={`p-1.5 rounded-md transition-colors cursor-pointer ${
              isOpen
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            }`}
            title="Thao tác"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
        );
      },
    },
  ];

  const filteredColumns = useMemo(() => {
    return columns.filter((col) => !col.key || col.key === 'actions' || visibleColumns.includes(col.key));
  }, [columns, visibleColumns]);

  return (
    <div className="space-y-4 md:space-y-5 min-h-full pb-8">
      {/* 2. HEADER TOPBAR & NÚT THAO TÁC */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 m-0">Quản lý gia hạn phản ánh</h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1 mb-0">
            Theo dõi, thẩm định và phê duyệt các yêu cầu xin gia hạn thời hạn giải quyết phản ánh
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={handleRefresh}
            className="h-9 px-3.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-xs md:text-sm font-semibold text-gray-700 shadow-xs flex items-center gap-1.5 transition-all cursor-pointer"
          >
            <RotateCcw className="w-4 h-4 text-gray-500" />
            <span>Làm mới</span>
          </button>
        </div>
      </div>

      {/* 3. KHỐI 4 THẺ STAT CARDS LỌC TRẠNG THÁI GIA HẠN */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {/* Card 1: Tổng số đề nghị */}
        <div
          onClick={() => { setStatusFilter('ALL'); setCurrentPage(1); }}
          className={`bg-white rounded-lg p-4 md:p-4.5 border shadow-xs transition-all cursor-pointer ${statusFilter === 'ALL'
              ? 'border-blue-500 ring-1 ring-blue-500/30 shadow-xs bg-blue-50/20'
              : 'border-gray-200 hover:border-gray-300'
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Tổng số đề nghị</span>
              <span className="text-2xl font-bold text-gray-900 tracking-tight">{statCounts.all}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 2: Chờ phê duyệt */}
        <div
          onClick={() => { setStatusFilter('PENDING'); setCurrentPage(1); }}
          className={`bg-white rounded-lg p-4 md:p-4.5 border shadow-xs transition-all cursor-pointer ${statusFilter === 'PENDING'
              ? 'border-orange-500 ring-1 ring-orange-500/30 shadow-xs bg-orange-50/20'
              : 'border-gray-200 hover:border-gray-300'
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Chờ phê duyệt</span>
              <span className="text-2xl font-bold text-orange-600 tracking-tight">{statCounts.pending}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 3: Đã phê duyệt */}
        <div
          onClick={() => { setStatusFilter('APPROVED'); setCurrentPage(1); }}
          className={`bg-white rounded-lg p-4 md:p-4.5 border shadow-xs transition-all cursor-pointer ${statusFilter === 'APPROVED'
              ? 'border-emerald-500 ring-1 ring-emerald-500/30 shadow-xs bg-emerald-50/20'
              : 'border-gray-200 hover:border-gray-300'
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Đã phê duyệt</span>
              <span className="text-2xl font-bold text-emerald-600 tracking-tight">{statCounts.approved}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 4: Đã từ chối */}
        <div
          onClick={() => { setStatusFilter('REJECTED'); setCurrentPage(1); }}
          className={`bg-white rounded-lg p-4 md:p-4.5 border shadow-xs transition-all cursor-pointer ${statusFilter === 'REJECTED'
              ? 'border-rose-500 ring-1 ring-rose-500/30 shadow-xs bg-rose-50/20'
              : 'border-gray-200 hover:border-gray-300'
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Đã từ chối</span>
              <span className="text-2xl font-bold text-rose-600 tracking-tight">{statCounts.rejected}</span>
            </div>
            <div className="w-9 h-9 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center">
              <XCircle className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>

      {/* 4. THANH BỘ LỌC TÌM KIẾM ĐỀ NGHỊ GIA HẠN */}
      <BaseFilter
        fields={filterFields}
        initialFilters={{
          searchQuery,
          statusFilter,
          selectedUrgency,
          selectedDepartment,
        }}
        onFilter={(newFilters) => {
          if (newFilters.searchQuery !== undefined) setSearchQuery(newFilters.searchQuery);
          if (newFilters.statusFilter !== undefined) setStatusFilter(newFilters.statusFilter);
          if (newFilters.selectedUrgency !== undefined) setSelectedUrgency(newFilters.selectedUrgency);
          if (newFilters.selectedDepartment !== undefined) setSelectedDepartment(newFilters.selectedDepartment);
          setCurrentPage(1);
        }}
        onReset={handleResetFilters}
        searchPlaceholder="Tìm theo mã phản ánh..."
        extraActions={columnSettingComponent}
      />

      {/* 5. BẢNG DỮ LIỆU ĐỀ NGHỊ GIA HẠN */}
      <div className="bg-white rounded-lg shadow-xs border border-gray-200 overflow-hidden">
        <BaseTable
          data={paginatedData}
          columns={filteredColumns}
          loading={loading}
          showActions={false}
          emptyMessage="Không có yêu cầu xin gia hạn nào"
          pagination={{
            current: currentPage,
            pageSize,
            total: filteredData.length,
            totalPages: Math.ceil(filteredData.length / pageSize),
            onChange: (p) => setCurrentPage(p),
          }}
        />
      </div>

      {/* 6. MODAL THẨM ĐỊNH & PHÊ DUYỆT GIA HẠN */}
      <ExtensionApproveModal
        isOpen={isApproveModalOpen}
        onClose={() => {
          setIsApproveModalOpen(false);
          setSelectedExtension(null);
        }}
        extension={selectedExtension}
        onSuccess={handleRefresh}
      />

      {/* 7. MODAL XEM CHI TIẾT PHẢN ÁNH */}
      {isDetailModalOpen && (
        <ReportDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedReportDetail(null);
          }}
          report={selectedReportDetail}
          mode={detailModalMode}
          onStatusUpdated={fetchExtensions}
        />
      )}

      {/* FLOATING ACTION MENU PORTAL */}
      {activeActionMenu && typeof document !== 'undefined' && createPortal(
        <div
          style={activeActionMenu.style}
          onClick={(e) => e.stopPropagation()}
          className="w-52 bg-white rounded-lg shadow-2xl border border-gray-200 py-1.5 animate-in fade-in zoom-in-95 duration-100 select-none text-left z-[99999]"
        >
          <button
            type="button"
            onClick={() => {
              const rec = activeActionMenu.record;
              setActiveActionMenu(null);
              handleOpenDetailModal(rec, 'view');
            }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors cursor-pointer text-left"
          >
            <Eye className="w-4 h-4 text-blue-600 shrink-0" />
            <span>Xem chi tiết</span>
          </button>

          {(() => {
            const stUpper = String(activeActionMenu.record.trang_thai || activeActionMenu.record.status || '').toUpperCase();
            const isPending = stUpper === 'PENDING' || stUpper.includes('CHỜ');
            const isApproved = stUpper === 'APPROVED' || stUpper.includes('ĐÃ DUYỆT') || stUpper.includes('ĐÃ PHÊ DUYỆT');

            return (
              <>
                {isPending && (
                  <button
                    type="button"
                    onClick={() => {
                      const rec = activeActionMenu.record;
                      setActiveActionMenu(null);
                      handleOpenApproveModal(rec);
                    }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-amber-700 hover:bg-amber-50 hover:text-amber-800 transition-colors cursor-pointer text-left font-medium"
                  >
                    <FileText className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>Thẩm định gia hạn</span>
                  </button>
                )}

                {isApproved && (
                  <button
                    type="button"
                    onClick={() => {
                      const rec = activeActionMenu.record;
                      setActiveActionMenu(null);
                      handleOpenDetailModal(rec, 'edit');
                    }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs md:text-sm text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 transition-colors cursor-pointer text-left font-medium"
                  >
                    <ToggleLeft className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Cập nhật trạng thái</span>
                  </button>
                )}
              </>
            );
          })()}
        </div>,
        document.body
      )}
    </div>
  );
}
